import { Button, Input, Label, toast } from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import {
  CONTENT_MODE_OPTIONS,
  type LuckContentMode,
} from "@/modules/chat-tools/domain/luck-printer/content-modes";
import {
  DEVICE_PROFILE_OPTIONS,
  getDeviceProfile,
  type DeviceProfileId,
} from "@/modules/chat-tools/domain/luck-printer/device-profiles";
import {
  PAPER_MODE_OPTIONS,
  paperModeUsesFeedDots,
  type LuckPaperMode,
} from "@/modules/chat-tools/domain/luck-printer/print-modes";
import type { LuckPrinterDensity } from "@/modules/chat-tools/domain/luck-printer/protocol";
import {
  clearSavedLuckPrinterDevice,
  getSavedLuckPrinterContentMode,
  getSavedLuckPrinterDevice,
  getSavedLuckPrinterDeviceProfile,
  getSavedLuckPrinterDensity,
  getSavedLuckPrinterFeedDots,
  getSavedLuckPrinterPaperMode,
  saveLuckPrinterContentMode,
  saveLuckPrinterDensity,
  saveLuckPrinterDeviceProfile,
  saveLuckPrinterFeedDots,
  saveLuckPrinterPaperMode,
} from "@/modules/chat-tools/domain/luck-printer/storage";
import { isWebBluetoothSupported } from "@/modules/chat-tools/presentation/lib/web-bluetooth";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEffect, useRef, useState } from "react";

const DENSITY_OPTIONS = [
  { value: "0", label: "ເບົາ" },
  { value: "1", label: "ປົກກະຕິ" },
  { value: "2", label: "ເຂັ້ມ" },
];

const CONTENT_MODE_SELECT_OPTIONS = CONTENT_MODE_OPTIONS.map(
  ({ value, label }) => ({ value, label }),
);

const PAPER_MODE_SELECT_OPTIONS = PAPER_MODE_OPTIONS.map(({ value, label }) => ({
  value,
  label,
}));

export function LabelPrinterSettingsSection() {
  const bluetoothSupported = isWebBluetoothSupported();
  const skipProfileSuggestion = useRef(true);
  const [pairedDevice, setPairedDevice] = useState(getSavedLuckPrinterDevice);
  const [profileId, setProfileId] = useState<DeviceProfileId>(
    getSavedLuckPrinterDeviceProfile(),
  );
  const [contentMode, setContentMode] = useState<LuckContentMode>(
    getSavedLuckPrinterContentMode(),
  );
  const [paperMode, setPaperMode] = useState<LuckPaperMode>(
    getSavedLuckPrinterPaperMode(),
  );
  const [density, setDensity] = useState<LuckPrinterDensity>(
    getSavedLuckPrinterDensity(),
  );
  const [feedDotsInput, setFeedDotsInput] = useState(
    String(getSavedLuckPrinterFeedDots()),
  );

  const profile = getDeviceProfile(profileId);
  const selectedContentMode =
    CONTENT_MODE_OPTIONS.find((option) => option.value === contentMode) ??
    CONTENT_MODE_OPTIONS[0];
  const selectedPaperMode =
    PAPER_MODE_OPTIONS.find((option) => option.value === paperMode) ??
    PAPER_MODE_OPTIONS[0];

  useEffect(() => {
    if (skipProfileSuggestion.current) {
      skipProfileSuggestion.current = false;
      return;
    }

    const nextProfile = getDeviceProfile(profileId);
    setPaperMode(nextProfile.suggestedPaperMode);
    setFeedDotsInput(String(nextProfile.defaultFeedDots));
  }, [profileId]);

  function handleSave() {
    const feedDots = Number.parseInt(feedDotsInput, 10);
    if (
      paperModeUsesFeedDots(paperMode) &&
      (!Number.isFinite(feedDots) || feedDots < 0 || feedDots > 255)
    ) {
      toast.error("feed dots ຕ້ອງເປັນ 0–255");
      return;
    }

    saveLuckPrinterDeviceProfile(profileId);
    saveLuckPrinterContentMode(contentMode);
    saveLuckPrinterPaperMode(paperMode);
    saveLuckPrinterDensity(density);
    if (paperModeUsesFeedDots(paperMode)) {
      saveLuckPrinterFeedDots(feedDots);
    }

    toast.success("ບັນທຶກການຕັ້ງຄ່າເຄື່ອງພິມແລ້ວ");
  }

  function handleForgetDevice() {
    clearSavedLuckPrinterDevice();
    setPairedDevice(null);
    toast.success("ລືມເຄື່ອງພິມແລ້ວ");
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <h3 className="font-display text-lg">ເຄື່ອງພິມປ້າຍ</h3>
        <p className="text-muted-foreground text-sm">
          ຕັ້ງຄ່າ Bluetooth ສຳລັບ Luck Jingle / Mini Sticker — ເກັບໃນ browser
          ນີ້ເທົ່ານັ້ນ
        </p>
      </div>

      {!bluetoothSupported ? (
        <p className="rounded-xl border border-secondary/20 bg-muted/20 px-3 py-2.5 text-muted-foreground text-sm">
          Browser ນີ້ບໍ່ຮອງຮັບ Web Bluetooth — ໃຊ້ Chrome desktop ຫຼື
          ເປີດປ້າຍທີລະອໍເດີ້ແທນ
        </p>
      ) : null}

      <FieldLayout>
        <FieldLabel>ຮຸ່ນເຄື່ອງພິມ</FieldLabel>
        <FieldContent>
          <SimpleSelect
            value={profileId}
            onValueChange={(value) =>
              setProfileId(value as DeviceProfileId)
            }
            options={DEVICE_PROFILE_OPTIONS}
          />
        </FieldContent>
      </FieldLayout>

      <FieldLayout>
        <FieldLabel>Content mode</FieldLabel>
        <FieldContent>
          <SimpleSelect
            value={contentMode}
            onValueChange={(value) =>
              setContentMode(value as LuckContentMode)
            }
            options={CONTENT_MODE_SELECT_OPTIONS}
          />
        </FieldContent>
        <p className="text-muted-foreground text-xs">
          {selectedContentMode.hint}
        </p>
      </FieldLayout>

      <FieldLayout>
        <FieldLabel>Paper mode</FieldLabel>
        <FieldContent>
          <SimpleSelect
            value={paperMode}
            onValueChange={(value) => setPaperMode(value as LuckPaperMode)}
            options={PAPER_MODE_SELECT_OPTIONS}
          />
        </FieldContent>
        <p className="text-muted-foreground text-xs">
          {selectedPaperMode.hint}
        </p>
      </FieldLayout>

      <FieldLayout>
        <FieldLabel>ຄວາມເຂັ້ມເລີ່ມຕົ້ນ</FieldLabel>
        <FieldContent>
          <SimpleSelect
            value={String(density)}
            onValueChange={(value) =>
              setDensity(Number(value) as LuckPrinterDensity)
            }
            options={DENSITY_OPTIONS}
          />
        </FieldContent>
      </FieldLayout>

      {paperModeUsesFeedDots(paperMode) ? (
        <FieldLayout>
          <FieldLabel>Feed dots ຫຼັງພິມ (0–255)</FieldLabel>
          <FieldContent>
            <Input
              type="number"
              min={0}
              max={255}
              value={feedDotsInput}
              onChange={(e) => setFeedDotsInput(e.target.value)}
            />
          </FieldContent>
          <p className="text-muted-foreground text-xs">
            ຄ່າເລີ່ມຕົ້ນ {profile.defaultFeedDots} — ປັບຖ້າຕຳແໜ່ງບໍ່ຕົງ
          </p>
        </FieldLayout>
      ) : null}

      <div className="rounded-xl border border-secondary/20 bg-muted/20 px-3 py-2.5 text-sm">
        <Label className="text-muted-foreground text-xs">ເຄື່ອງທີ່ຈື່ໄວ້</Label>
        <p className="mt-1 font-medium">
          {pairedDevice?.name ?? "ຍັງບໍ່ເຄີຍຈັບຄູ່"}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={handleSave}>
          ບັນທຶກການຕັ້ງຄ່າ
        </Button>
        {pairedDevice ? (
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={handleForgetDevice}
          >
            ລືມເຄື່ອງພິມ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
